import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, Bell } from "lucide-react";

export default function Settings() {
  const [me, setMe] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/me");
      setMe(r.data);
    } catch { toast.error("Failed to load your settings"); }
  };
  useEffect(() => { load(); }, []);

  const toggleDigest = async (nextOptout) => {
    setSaving(true);
    try {
      await api.put("/me/digest-optout", { optout: nextOptout });
      setMe(m => ({ ...m, digest_optout: nextOptout }));
      toast.success(nextOptout ? "Digest emails paused" : "Digest emails resumed");
    } catch {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  if (!me) return <AppShell><div className="p-6 text-slate-500">Loading…</div></AppShell>;
  const digestOn = !me.digest_optout;

  return (
    <AppShell>
      <div>
        <div className="eyebrow">Your account</div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-1">Settings</h1>
        <p className="text-slate-500 text-sm">Control how Informed Accelerator communicates with you.</p>
      </div>

      <Card className="glass rounded-2xl mt-6">
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2">
            <Bell size={16} className="text-purple-700"/>
            <div className="text-slate-900 font-semibold">Email notifications</div>
          </div>

          <div className="flex items-start justify-between gap-4 pt-1">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 h-9 w-9 rounded-2xl grid place-items-center bg-purple-50 text-purple-700 ring-1 ring-purple-100 shrink-0">
                <Mail size={16}/>
              </div>
              <div>
                <Label className="text-slate-900 font-medium">Overdue follow-up digest</Label>
                <p className="text-xs text-slate-500 mt-1 max-w-md leading-relaxed">
                  Once per day we email you the disputes whose 30-day follow-up window just lapsed, so nothing slips.
                  Turn this off if you'd rather manage follow-ups yourself.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className={`text-xs font-medium ${digestOn ? "text-emerald-700" : "text-slate-500"}`}>
                {digestOn ? "On" : "Paused"}
              </span>
              <Switch
                checked={digestOn}
                onCheckedChange={(v) => toggleDigest(!v)}
                disabled={saving}
                data-testid="settings-digest-toggle"
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </AppShell>
  );
}
